import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Wallet, Clock, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hero strip for LOGGED-IN GUEST users — surfaces wallet balance + earliest expiry.
 * Hidden for anonymous / hotel users (keeps hero free of clutter for new visitors).
 */
export const WalletHeroStrip = () => {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'guest') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/guest/profile`, { withCredentials: true });
        if (!cancelled) setInfo(data.wallet || null);
      } catch (err) { console.error('[WalletHeroStrip] profile fetch failed:', err); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || user.role !== 'guest' || !info) return null;
  const balance = Number(info.balance || 0);
  if (balance <= 0) return null;

  // Earliest expiry — present in info.next_expiry_at or info.transactions list
  let daysLeft = null;
  let expiringAmount = null;
  if (info.next_expiry_at && info.next_expiry_amount) {
    const exp = new Date(info.next_expiry_at);
    daysLeft = Math.max(0, Math.ceil((exp.getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
    expiringAmount = Number(info.next_expiry_amount);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6" data-testid="wallet-hero-strip">
      <div className="rounded-2xl bg-gradient-to-r from-[#FFE5EE] via-[#FFF9DB] to-[#FFE5EE] border border-[#FF0059]/20 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-full bg-[#FF0059] text-white flex items-center justify-center flex-shrink-0">
            <Wallet className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-black text-black" data-testid="hero-wallet-balance">₹{balance.toLocaleString('en-IN')}</span>
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700">in your wallet</span>
            </div>
            {daysLeft !== null && expiringAmount > 0 ? (
              <p className="text-xs text-[#CC0047] flex items-center gap-1 mt-0.5 font-semibold" data-testid="hero-wallet-expiry">
                <Clock className="h-3 w-3" />
                <span>
                  ₹{expiringAmount.toLocaleString('en-IN')} expires in {daysLeft} day{daysLeft === 1 ? '' : 's'}
                </span>
              </p>
            ) : (
              <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5 font-semibold">
                <Sparkles className="h-3 w-3 text-[#FF0059]" /> Redeem on any booking · valid for 30 days from earning
              </p>
            )}
          </div>
        </div>
        <Link
          to="/profile"
          className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full bg-black hover:bg-[#FF0059] text-white text-xs font-black transition-colors whitespace-nowrap"
          data-testid="hero-wallet-cta"
        >
          View wallet
        </Link>
      </div>
    </div>
  );
};
